// ThemePreview.tsx
// テーマプレビューコンポーネント（設定を即座に反映）

import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PreviewIcon from '@mui/icons-material/Visibility';
import { ApplicationType } from '../data/ApplicationType';
import { iconMap } from '../../HomePage/Components/ApplicationSelect/iconMap';
import muiTheme from '../../../style/theme';

interface ThemePreviewProps {
  typeName: string;
  theme: ApplicationType['theme'];
}

/**
 * テーマプレビューコンポーネント（申請種別設定用）
 * - 設定中のアイコン・ボタン色をホームページの申請書一覧と同じスタイルでリアルタイムプレビューする
 * - props: typeName (申請種別名), theme (プレビューするテーマデータ)
 */
export default function ThemePreview({ typeName, theme }: ThemePreviewProps) {
  const IconComponent = iconMap[theme.iconName] || iconMap['MoreHorizRoundedIcon'];

  return (
    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
      {/* 全体プレビュー Chip */}
      <Box sx={{ mb: 2 }}>
        <Chip icon={<PreviewIcon />} label="全体プレビュー" color="primary" size="small" />
      </Box>

      {/* HomePage の ApplicationSelect と同じスタイルでプレビュー */}
      <Box
        sx={{
          bgcolor: muiTheme.palette.secondary.main,
          p: 1,
          borderRadius: 1,
        }}
      >
        <Accordion
          expanded={true}
          disableGutters
          sx={{
            boxShadow: 1,
            '&:before': {
              display: 'none',
            },
          }}
        >
          {/* アコーディオンヘッダー */}
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              bgcolor: muiTheme.palette.common.white,
              p: 2,
            }}
          >
            <Box display="flex" alignItems="center">
              <Avatar
                sx={{
                  bgcolor: theme.iconBgColor,
                  width: 20,
                  height: 20,
                  mr: 1,
                  border: `2px solid ${theme.iconBdColor || theme.iconBgColor}`,
                }}
              >
                <IconComponent
                  style={{
                    color: theme.iconColor,
                    width: 15,
                    height: 15,
                  }}
                />
              </Avatar>
              <Typography variant="h6" fontSize="0.9rem">
                {typeName}
              </Typography>
            </Box>
          </AccordionSummary>

          {/* アコーディオン内容（ボタンプレビュー） */}
          <AccordionDetails>
            <Box
              sx={{
                bgcolor: muiTheme.palette.secondary.main,
                p: 0.5,
              }}
            >
              {/* サンプルボタン1 */}
              <Button
                sx={{
                  bgcolor: theme.buttonColor,
                  color: theme.fontColor,
                  width: '100%',
                  textAlign: 'left',
                  border: `1px solid ${theme.buttonBdColor || theme.buttonColor}`,
                  borderRadius: 1,
                  py: 0.5,
                  height: '60px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
                  mb: 0.6,
                  '&:hover': {
                    bgcolor: theme.buttonColor,
                    opacity: 0.9,
                  },
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    mb: 0.5,
                    fontWeight: 'bold',
                    fontSize: '0.875rem',
                    color: theme.fontColor,
                  }}
                >
                  申請書サンプル1
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.fontColor,
                    fontSize: '0.75rem',
                    opacity: 0.7,
                  }}
                >
                  これは説明文のサンプルです
                </Typography>
              </Button>

              {/* サンプルボタン2 */}
              <Button
                sx={{
                  bgcolor: theme.buttonColor,
                  color: theme.fontColor,
                  width: '100%',
                  textAlign: 'left',
                  border: `1px solid ${theme.buttonBdColor || theme.buttonColor}`,
                  borderRadius: 1,
                  py: 0.5,
                  height: '60px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
                  '&:hover': {
                    bgcolor: theme.buttonColor,
                    opacity: 0.9,
                  },
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    mb: 0.5,
                    fontWeight: 'bold',
                    fontSize: '0.875rem',
                    color: theme.fontColor,
                  }}
                >
                  申請書サンプル2
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.fontColor,
                    fontSize: '0.75rem',
                    opacity: 0.7,
                  }}
                >
                  ボタンの見た目を確認できます
                </Typography>
              </Button>
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>

    </Paper>
  );
}
